import type { RankMapPage, RankMeta, RankMetric, RankQueueItem, RankSubmission, RankSubmissionDraft } from '@/domain/types'
import { db } from '@/services/database'

const RETRY_BASE_MS = 30_000
const RETRY_MAX_MS = 6 * 60 * 60 * 1_000
const QUEUE_BATCH_SIZE = 10
const RANK_PAGE_SIZE = 10
const AUTH_REFRESH_SKEW_MS = 30_000
const RANK_MOCK_ENABLED = import.meta.env.DEV
  && import.meta.env.MODE !== 'test'
  && import.meta.env.VITE_RANK_USE_MOCK !== 'false'

let fingerprintPromise: Promise<string> | null = null
let flushPromise: Promise<void> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let accessToken: { value: string; expiresAt: number } | null = null
let accessTokenPromise: Promise<{ value: string; expiresAt: number }> | null = null
let initialized = false

export class RankApiError extends Error {
  constructor(
    readonly code: 'network' | 'response',
    message: string,
    readonly status: number | null = null,
  ) {
    super(message)
    this.name = 'RankApiError'
  }
}

export function rankApiBaseUrl(): string {
  const configured = import.meta.env.VITE_RANK_API_BASE_URL?.trim().replace(/\/+$/u, '')
  return configured ?? ''
}

export async function enqueueRankSubmission(submission: RankSubmissionDraft): Promise<void> {
  if (RANK_MOCK_ENABLED) return
  const now = Date.now()
  const item: RankQueueItem = {
    id: submission.submissionId,
    submission,
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    nextAttemptAt: now,
  }
  await db.rankQueue.put(item)
  void flushRankQueue()
}

export function initializeRankSync(): void {
  if (RANK_MOCK_ENABLED) return
  if (initialized) return
  initialized = true
  window.addEventListener('online', handleOnline)
  void flushRankQueue()
}

export async function flushRankQueue(): Promise<void> {
  if (RANK_MOCK_ENABLED) return
  if (flushPromise) return flushPromise
  clearRetryTimer()
  flushPromise = flushDueItems()
    .finally(() => {
      flushPromise = null
      void scheduleNextFlush()
    })
  return flushPromise
}

export async function listRankMaps(options: {
  presetId: string
  metric: RankMetric
  levelBucket: number
  page: number
  pageSize?: number
  signal?: AbortSignal
}): Promise<RankMapPage> {
  if (RANK_MOCK_ENABLED) {
    const { createMockRankPage } = await import('@/services/rank-mock')
    return createMockRankPage({
      metric: options.metric,
      levelBucket: options.levelBucket,
      page: options.page,
      pageSize: options.pageSize ?? RANK_PAGE_SIZE,
    })
  }
  const params = new URLSearchParams({
    presetId: options.presetId,
    metric: options.metric,
    levelBucket: String(options.levelBucket),
    page: String(options.page),
    pageSize: String(options.pageSize ?? RANK_PAGE_SIZE),
  })
  return requestJson<RankMapPage>(`/api/v1/rank/maps?${params}`, { signal: options.signal })
}

export async function getRankMeta(presetId: string, signal?: AbortSignal): Promise<RankMeta> {
  if (RANK_MOCK_ENABLED) {
    const { createMockRankMeta } = await import('@/services/rank-mock')
    return createMockRankMeta(presetId)
  }
  const params = new URLSearchParams({ presetId })
  return requestJson<RankMeta>(`/api/v1/rank/meta?${params}`, { signal })
}

async function flushDueItems(): Promise<void> {
  if (!navigator.onLine) return

  while (navigator.onLine) {
    const due = await db.rankQueue.where('nextAttemptAt').belowOrEqual(Date.now()).limit(QUEUE_BATCH_SIZE).toArray()
    if (due.length === 0) return

    let visitorId: string
    try {
      visitorId = await getVisitorId()
    } catch (caught) {
      await Promise.all(due.map((item) => rescheduleQueueItem(item, caught)))
      return
    }

    for (const item of due) {
      try {
        await submitRank({ ...item.submission, visitorId })
        await db.rankQueue.delete(item.id)
      } catch (caught) {
        if (isPermanentSubmissionError(caught)) {
          await db.rankQueue.delete(item.id)
          continue
        }
        await rescheduleQueueItem(item, caught)
      }
    }
  }
}

async function getVisitorId(): Promise<string> {
  fingerprintPromise ??= import('@fingerprintjs/fingerprintjs')
    .then(({ default: FingerprintJS }) => FingerprintJS.load())
    .then((agent) => agent.get())
    .then((result) => result.visitorId)
    .catch((caught) => {
      fingerprintPromise = null
      throw caught
    })
  return fingerprintPromise
}

async function submitRank(submission: RankSubmission): Promise<void> {
  await requestJson('/api/v1/rank/submissions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(submission),
  })
}

async function requestJson<T = unknown>(path: string, init: RequestInit = {}, retryAuth = true): Promise<T> {
  const baseUrl = rankApiBaseUrl()

  const token = await getAccessToken()
  const headers = new Headers(init.headers)
  headers.set('authorization', `Bearer ${token.value}`)
  let response: Response
  try {
    response = await fetch(`${baseUrl}${path}`, { ...init, headers })
  } catch (caught) {
    throw new RankApiError('network', caught instanceof Error ? caught.message : String(caught))
  }
  if (response.status === 401 && retryAuth) {
    clearAccessToken()
    return requestJson<T>(path, init, false)
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new RankApiError('response', `${response.status} ${detail.slice(0, 160)}`.trim(), response.status)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

async function getAccessToken(): Promise<{ value: string; expiresAt: number }> {
  if (accessToken && accessToken.expiresAt - AUTH_REFRESH_SKEW_MS > Date.now()) return accessToken
  if (accessTokenPromise) return accessTokenPromise
  const baseUrl = rankApiBaseUrl()

  accessTokenPromise = fetch(`${baseUrl}/api/v1/auth/token`, { method: 'POST', headers: { accept: 'application/json' } })
    .catch((caught: unknown) => {
      throw new RankApiError('network', caught instanceof Error ? caught.message : String(caught))
    })
    .then(async (response) => {
      if (!response.ok) {
        const detail = await response.text().catch(() => '')
        throw new RankApiError('response', `${response.status} ${detail.slice(0, 160)}`.trim(), response.status)
      }
      const body = await response.json() as { token?: unknown; expiresAt?: unknown }
      if (typeof body.token !== 'string' || typeof body.expiresAt !== 'number') {
        throw new RankApiError('response', 'Invalid auth token response')
      }
      accessToken = { value: body.token, expiresAt: body.expiresAt }
      return accessToken
    })
    .finally(() => {
      accessTokenPromise = null
    })
  return accessTokenPromise
}

function clearAccessToken(): void {
  accessToken = null
  accessTokenPromise = null
}

function isPermanentSubmissionError(caught: unknown): boolean {
  if (!(caught instanceof RankApiError) || caught.code !== 'response' || caught.status === null) return false
  return [400, 403, 404, 405, 409, 410, 413, 415, 422].includes(caught.status)
}

async function rescheduleQueueItem(item: RankQueueItem, caught: unknown): Promise<void> {
  const attempts = item.attempts + 1
  const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.min(attempts - 1, 10))
  await db.rankQueue.put({
    ...item,
    attempts,
    updatedAt: Date.now(),
    nextAttemptAt: Date.now() + delay,
    lastError: caught instanceof Error ? caught.message.slice(0, 240) : String(caught).slice(0, 240),
  })
}

async function scheduleNextFlush(): Promise<void> {
  clearRetryTimer()
  if (!navigator.onLine) return
  const next = await db.rankQueue.orderBy('nextAttemptAt').first()
  if (!next) return
  const delay = Math.max(0, Math.min(RETRY_MAX_MS, next.nextAttemptAt - Date.now()))
  retryTimer = globalThis.setTimeout(() => { void flushRankQueue() }, delay)
}

function clearRetryTimer(): void {
  if (retryTimer !== null) globalThis.clearTimeout(retryTimer)
  retryTimer = null
}

function handleOnline(): void {
  void flushRankQueue()
}
